import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { useTranslation } from '../context/TranslationContext';
import faqData from '../data/faqData';
import faqItems from '../data/faqItems';
import SEO from '../components/SEO';
import { PAGE_SEO, faqSchema } from '../data/seoMeta';

var videoIndices = { 1: 'https://youtu.be/xqaLjkKhx8c', 4: 'https://youtube.com/shorts/KAGcRSbpRNg' };

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  var shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return 'https://www.youtube.com/embed/' + shortsMatch[1];
  return url;
}

function FAQPage() {
  var { language } = useTranslation();
  var [openIndex, setOpenIndex] = useState(null);
  var content = faqData[language] || faqData.en;
  var items = faqItems[language] || faqItems.en;

  function toggleFAQ(index) {
    setOpenIndex(openIndex === index ? null : index);
  }

  return (
    <Layout>
      <SEO
        title={PAGE_SEO.faq.title}
        description={PAGE_SEO.faq.description}
        url="/faq"
        schema={faqSchema((faqItems.en || [])
          // Answers that only say "watch this video" make a thin rich result.
          .filter(function(item) { return (item[2] || '').replace(/<[^>]*>/g, '').trim().length > 60; })
          .map(function(item) {
            return { q: item[1], a: item[2].replace(/<[^>]*>/g, '').trim() };
          }))}
      />
      <section className="pt-32 pb-20 bg-gradient-to-b from-neutral-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-4">{content.support}</p>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">{content.title}</h1>
          <p className="text-xl text-neutral-400 max-w-2xl mx-auto">{content.subtitle}</p>
        </div>
      </section>

      <section data-testid="faq-section" className="py-16 bg-black">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="space-y-3">
            {items.map(function(item, index) {
              var isOpen = openIndex === index;
              var video = videoIndices[index] || null;
              var isLast = index === items.length - 1;
              return (
                <div key={index} className="border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={function() { toggleFAQ(index); }}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-neutral-900/50 transition-colors"
                  >
                    <div className="flex items-start gap-4 pr-4">
                      <span className="text-xs uppercase tracking-widest text-[#d4af37] font-medium whitespace-nowrap mt-1">{item[0]}</span>
                      <h3 className="text-lg font-medium text-white">{item[1]}</h3>
                    </div>
                    <ChevronDown className={'w-5 h-5 text-neutral-500 flex-shrink-0 transition-transform duration-300 ' + (isOpen ? 'rotate-180' : '')} />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6">
                      <div className="pl-0 md:pl-28">
                        <p className="text-neutral-300 leading-relaxed whitespace-pre-line">{item[2]}</p>
                        {video && (
                          <div className="mt-4 max-w-md">
                            <iframe
                              src={getYouTubeEmbedUrl(video)}
                              title={item[1]}
                              className="w-full aspect-video rounded-xl"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        )}
                        {isLast && (
                          <p className="mt-4 pt-4 border-t border-white/10 text-neutral-500 text-xs italic">{content.importTaxDisclaimer}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center p-8 rounded-2xl bg-neutral-900/50 border border-white/5">
            <HelpCircle className="w-10 h-10 text-[#d4af37] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-3">{content.stillQuestions}</h3>
            <p className="text-neutral-400 mb-6">{content.stillQuestionsDesc}</p>
            <a
              href="/contact"
              data-testid="faq-contact-link"
              className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-6 py-3 rounded-full font-semibold hover:bg-white transition-all"
            >
              {content.contactUs}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default FAQPage;
